import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, useResponsive } from '@common-fe/common-fe';

import ROUTES from '@/common/routes';
import { useBreadcrumbs } from '@/modules/core/hooks';
import { FixYourAddressWarningCard } from '@/modules/transaction/components/ReimburseMe/ConfirmationStep/Immediately';
import { useStore } from '@/modules/transaction/components/ReimburseMe/store/usePaymentMethods.store';
import { CreateClaimDto, CreatedClaimDto, CreatedHsaWithdrawalClaimDto, CreateHsaWithdrawalClaimDto } from '@/modules/UseMyMoney/queries';
import { useMyMoneyFormStore, useMyMoneyStore } from '@/modules/UseMyMoney/stores';
import {
  ClaimSummaryField,
  UseMyMoneyOption,
  UseMyMoneySummaryTitle,
} from '@/modules/UseMyMoney/UseMyMoney.types';
import AccountsDetailsContainer from '@/modules/UseMyMoney/widgets/AccountDetails/AccountDetails.container';
import DefaultPaymentMethodContainer from '@/modules/UseMyMoney/widgets/DefaultPaymentMethod/DefaultPaymentMethodContainer';
import ProviderPaymentMethodContainer from '@/modules/UseMyMoney/widgets/ProviderPaymentMethod/ProviderPaymentMethod.container';
import DocumentPreview from '@/modules/UseMyMoney/widgets/UseMyMoneySummary/components/DocumentUploadCard/DocumentPreview';
import DocumentUploadCard from '@/modules/UseMyMoney/widgets/UseMyMoneySummary/components/DocumentUploadCard/DocumentUploadCard';
import useEmployeeStore from '@/modules/user/stores/useEmployee.store';
import { PaymentMethodType } from '@/modules/user/user.types';

import ClaimSummaryFields from '../ClaimSummaryFields/ClaimSummaryFields';
import ClaimSummaryFooter from '../ClaimSummaryFooter/ClaimSummaryFooter';
import ClaimSummaryHeader from '../ClaimSummaryHeader/ClaimSummaryHeader';
import ClaimSummaryOptionalFields from '../ClaimSummaryOptionalFields/ClaimSummaryOptionalFields';
import ClaimSummaryPaymentOrder from '../ClaimSummaryPaymentOrder/ClaimSummaryPaymentOrder';
import ClaimSummaryProofOfNecessity from '../ClaimSummaryProofOfNecessity/ClaimSummaryProofOfNecessity';

interface Props {
  isAccountsLoading?: boolean;
  setIsSelectLmnPopupVisible?: (value: boolean) => void;
  onDeleteLmn: (lmnId?: string) => Promise<unknown>;
  setActiveField: (filed?: ClaimSummaryField) => void;
  createClaim: (claimDto: CreateClaimDto) => Promise<CreatedClaimDto>;
  createHsaWithdrawalClaim: (claimDto: CreateHsaWithdrawalClaimDto) => Promise<CreatedHsaWithdrawalClaimDto>;
  onUploadFile: () => void;
  onDeleteFile?: (fileId: string) => Promise<unknown>;
  onDeleteDocument?: (documentId: string) => Promise<unknown>;
  setIsSuccessPopupVisible: (value: boolean) => void;
  isCreateClaimLoading?: boolean;
  isCreateHsaWithdrawalClaimLoading?: boolean;
  hasAvailableAccounts?: boolean;
  isUploadNewFilePopupVisible?: boolean;
}

const ClaimSummary: React.FC<Props> = ({
  isAccountsLoading,
  setIsSelectLmnPopupVisible,
  onDeleteLmn,
  setActiveField,
  createClaim,
  createHsaWithdrawalClaim,
  onUploadFile,
  onDeleteFile,
  onDeleteDocument,
  setIsSuccessPopupVisible,
  isCreateClaimLoading,
  isCreateHsaWithdrawalClaimLoading,
  hasAvailableAccounts,
  isUploadNewFilePopupVisible,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { useMyMoneyOption } = useMyMoneyStore();
  const { values } = useMyMoneyFormStore();
  const { employee } = useEmployeeStore();
  const { selectedMethod } = useStore();
  const title = useMyMoneyOption ? t(UseMyMoneySummaryTitle?.[useMyMoneyOption]) : t('Claim Summary');

  const [isPreviewDocumentPopupOpen, setIsPreviewDocumentPopupOpen] = useState(false);

  useBreadcrumbs(
    [
      {
        key: ROUTES.HOME,
        title: t('Home'),
        value: ROUTES.HOME,
      },
      {
        key: ROUTES.USE_MY_MONEY,
        title: t('Use My Money'),
        value: ROUTES.USE_MY_MONEY,
      },
      {
        key: ROUTES.USE_MY_MONEY_SUMMARY,
        title,
        mobileTitle: title,
        value: ROUTES.USE_MY_MONEY_SUMMARY,
      },
    ],
    true
  );

  const handleOpenPreviewPopup = useCallback(() => {
    setIsPreviewDocumentPopupOpen(true);
  }, []);

  const handleClosePreviewPopup = useCallback(() => {
    setIsPreviewDocumentPopupOpen(false);
  }, []);

  const hasUndeliverableAddress = useMemo(() => employee?.addresses?.find((address) => address?.undeliverable && address?.preferredMailing),
    [employee?.addresses]);

  const hasUndeliverableAddressError = useMemo(
    () =>
      useMyMoneyOption === UseMyMoneyOption.REIMBURSE_MYSELF &&
      hasUndeliverableAddress &&
      selectedMethod?.type === PaymentMethodType.CHECK,
    [useMyMoneyOption, hasUndeliverableAddress, selectedMethod?.type]
  );

  return (
    <Box
      data-testId="claim-summary"
      direction="row"
      gap="spacing24"
      style={{ position: 'relative'}}
    >
      <Box
        direction="column"
        gap={isMobile ? 'spacing16' : 'none'}
        width={{ width: '100%' }}
      >
        <ClaimSummaryHeader useMyMoneyOption={useMyMoneyOption} />

        {isMobile && (
          <DocumentUploadCard
            onOpenPreview={handleOpenPreviewPopup}
            onUploadFile={onUploadFile}
          />
        )}

        <ClaimSummaryFields
          margin={isMobile ? undefined : { top: 'spacing24' }}
          useMyMoneyOption={useMyMoneyOption}
          setActiveField={setActiveField}
          onUploadFile={onUploadFile}
          hasAvailableAccounts={hasAvailableAccounts}
        />
        <ClaimSummaryOptionalFields margin={isMobile ? undefined : { top: 'spacing24' }} />

        {(useMyMoneyOption === UseMyMoneyOption.REIMBURSE_MYSELF || useMyMoneyOption === UseMyMoneyOption.PAY_A_PROVIDER) && (
          <ClaimSummaryProofOfNecessity
            setIsSelectLmnPopupVisible={setIsSelectLmnPopupVisible}
            onDeleteLmn={onDeleteLmn}
          />
        )}

        {(useMyMoneyOption === UseMyMoneyOption.REIMBURSE_MYSELF || useMyMoneyOption === UseMyMoneyOption.HSA_WITHDRAWAL) && (
          <Box margin={isMobile ? undefined : { top: 'spacing24' }}>
            <DefaultPaymentMethodContainer />
          </Box>
        )}

        {useMyMoneyOption === UseMyMoneyOption.PAY_A_PROVIDER && (
          <ProviderPaymentMethodContainer />
        )}

        <FixYourAddressWarningCard
          storeKey="fix-your-address-warning-card_claim-summary"
          showWarning={hasUndeliverableAddressError}
          margin={{ top: 'spacing8' }}
        />

        {((values?.dateOfService && values?.patientId) || useMyMoneyOption === UseMyMoneyOption.HSA_WITHDRAWAL) && (
          <ClaimSummaryPaymentOrder
            margin={isMobile ? undefined : { top: 'spacing24' }}
            isLoading={isAccountsLoading}
            isHsaWithdrawal={useMyMoneyOption === UseMyMoneyOption.HSA_WITHDRAWAL}
          />
        )}

        <ClaimSummaryFooter
          margin={isMobile ? undefined : { top: 'spacing24' }}
          createClaim={createClaim}
          createHsaWithdrawalClaim={createHsaWithdrawalClaim}
          isCreateClaimLoading={isCreateClaimLoading}
          isCreateHsaWithdrawalClaimLoading={isCreateHsaWithdrawalClaimLoading}
          onUploadFile={onUploadFile}
          setIsSuccessPopupVisible={setIsSuccessPopupVisible}
          hasUndeliverableAddressError={hasUndeliverableAddressError}
        />
      </Box>

      {!isMobile && (
        <Box
          width={{ width: '100%', max: '300px' }}
          alignSelf='start'
          style={{ position: 'sticky', top: '24px' }}
        >
          <Box
            direction="column"
            gap="spacing24"
            style={{
              opacity: isPreviewDocumentPopupOpen ? 0 : 1,
              pointerEvents: isPreviewDocumentPopupOpen ? 'none' : 'auto',
              transition: 'opacity 0.2s ease-in-out',
            }}
          >
            <DocumentUploadCard onOpenPreview={handleOpenPreviewPopup} onUploadFile={onUploadFile} />
            <AccountsDetailsContainer />
          </Box>

          {isPreviewDocumentPopupOpen && (
            <DocumentPreview
              onClose={handleClosePreviewPopup}
              onUploadFile={onUploadFile}
              onDeleteFile={onDeleteFile}
              onDeleteDocument={onDeleteDocument}
              isClickOutsideBlocked={isUploadNewFilePopupVisible}
            />
          )}
        </Box>
      )}

      {isMobile && isPreviewDocumentPopupOpen && (
        <DocumentPreview
          onClose={handleClosePreviewPopup}
          onUploadFile={onUploadFile}
          onDeleteFile={onDeleteFile}
          onDeleteDocument={onDeleteDocument}
          isClickOutsideBlocked={isUploadNewFilePopupVisible}
        />
      )}
    </Box>
  );
};

export default ClaimSummary;
